<template>
  <div class="receipt-folder-container">
    <slot name="title" />

    <!-- Web-owned action bar (same pattern as cardtransactions) — mobile only;
         the desktop folder view keeps its own page chrome. -->
    <div v-if="isMobile" class="navless-layout-header">
      <slot name='navbar'>
        <ed-button :to='$routerHistory.previous().path'>
          <i class="fal fa-chevron-left"></i>
          &nbsp;
          Back
        </ed-button>
      </slot>
    </div>

    <!-- Feature flag check -->
    <div v-if="!featureFlags.receiptScanPrefill" class="feature-disabled">
      <p>Receipt folder feature is not available.</p>
    </div>

    <!-- Empty state -->
    <div v-else-if="receipts && !receipts.length && !isLoading" class="no-data">
      <div>
        <i class="icon fas fa-folder-open"></i>
        <h3>No Receipts</h3>
        <p>No receipts in your folder yet. Upload a receipt without picking a transaction, or tap "Save for later" during upload, and it'll land here for you to claim.</p>
        <ed-button @click="handleUploadClick" class="upload-cta">Submit Receipt</ed-button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else-if="isLoading" class="loading-state">
      <p>Loading receipts...</p>
    </div>

    <!-- Receipt list -->
    <div v-else @touchstart="onPullStart" @touchmove="onPullMove" @touchend="onPullEnd">
      <!-- Pull-to-refresh indicator (mobile) -->
      <div v-if="isMobile" class="pull-indicator" :style="{ height: pullDistance + 'px', opacity: Math.min(pullDistance / 60, 1) }">
        <div class="pull-indicator-content">
          <i v-if="isRefreshing" class="fas fa-spinner fa-spin"></i>
          <i v-else-if="pullDistance >= 60" class="fas fa-arrow-up"></i>
          <i v-else class="fas fa-arrow-down"></i>
          <span v-if="isRefreshing">Refreshing...</span>
          <span v-else-if="pullDistance >= 60">Release to refresh</span>
          <span v-else>Pull to refresh</span>
        </div>
      </div>

      <!-- Refresh button (web) -->
      <div v-if="!isMobile" class="refresh-controls">
        <ed-button @click="handleRefresh" :disabled="isRefreshing">
          {{ isRefreshing ? 'Refreshing...' : 'Refresh' }}
        </ed-button>
      </div>

      <!-- Sort control (mobile) — honors ticket text: most recent upload OR scanned receipt date. -->
      <div v-if="isMobile" class="sort-controls">
        <label class="sort-controls__label">Sort by:
          <select v-model="sortBy" @change="fetchReceipts" class="sort-controls__select">
            <option value="UploadedAt">Most recent upload</option>
            <option value="TransactionDate">Receipt date</option>
          </select>
        </label>
      </div>

      <!-- Receipt rows -->
      <div
        v-for="receipt in receipts"
        :key="receipt.receiptScanId"
        class="receipt-row"
        :class="{ 'is-processing': receipt.status === 'Processing', 'is-highlighted': receipt.receiptScanId === highlightedReceiptId }"
      >
        <!-- Mobile swipe-to-delete (suppressed for admins viewing other users' receipts) -->
        <div v-if="isMobile" class="swipe-container">
          <div v-if="canMutateReceipt(receipt)" class="swipe-delete-action" @click="handleDelete(receipt)">
            <i class="fas fa-trash"></i>
            <span>Delete</span>
          </div>
          <div class="swipe-content"
            :style="getSwipeStyle(receipt.receiptScanId)"
            @touchstart="canMutateReceipt(receipt) && onRowSwipeStart($event, receipt)"
            @touchmove="canMutateReceipt(receipt) && onRowSwipeMove($event, receipt)"
            @touchend="canMutateReceipt(receipt) && onRowSwipeEnd($event, receipt)"
          >
            <ed-notice class="receipt-notice">
              <div class="receipt-content">
                <!-- Thumbnail -->
                <div class="receipt-thumbnail">
                  <img v-if="imageUrlCache[receipt.receiptScanId]"
                       :src="imageUrlCache[receipt.receiptScanId]"
                       alt=""
                       class="receipt-thumbnail-img"
                       @error="onThumbnailError(receipt)" />
                  <i v-else class="icon fas fa-receipt"></i>
                </div>

                <!-- Receipt details -->
                <div class="receipt-details">
                  <div class="file-name">
                    <strong>{{ receipt.originalFileName }}</strong>
                  </div>
                  <div class="upload-date">
                    {{ formatDate(receipt.createdAt) }}
                  </div>

                  <!-- OCR summary -->
                  <div class="ocr-summary">
                    <span v-if="receipt.merchantName" class="merchant">{{ receipt.merchantName }}</span>
                    <span v-if="receipt.totalAmount" class="total">
                      <v-money>{{ receipt.totalAmount }}</v-money>
                    </span>
                  </div>

                  <!-- Top candidate hint -->
                  <div v-if="receipt.topCandidate" class="top-candidate-hint">
                    <small>
                      Suggested: {{ receipt.topCandidate.merchantName }}
                      <span v-if="receipt.topCandidate.amount">
                        <v-money>{{ receipt.topCandidate.amount }}</v-money>
                      </span>
                    </small>
                  </div>

                  <!-- Warnings badge -->
                  <div v-if="receipt.warnings && receipt.warnings.length" class="warnings-badge">
                    <span class="badge">{{ receipt.warnings.length }} warning<span v-if="receipt.warnings.length > 1">s</span></span>
                  </div>
                </div>

                <!-- Status and actions -->
                <div class="receipt-actions">
                  <div class="status-badge">
                    <span class="badge badge-success">Saved</span>
                  </div>

                  <!-- Action buttons -->
                  <div class="action-buttons">
                    <ed-button
                      @click="handleClaim(receipt)"
                      class="claim-btn"
                      :disabled="!canMutateReceipt(receipt)"
                      :title="readOnlyTooltip(receipt)">
                      Claim
                    </ed-button>
                  </div>
                </div>
              </div>
            </ed-notice>
          </div>
        </div>

        <!-- Web version with kebab menu -->
        <ed-notice v-if="!isMobile" class="receipt-notice">
          <div class="receipt-content">
            <!-- Thumbnail -->
            <div class="receipt-thumbnail">
              <i class="icon fas fa-receipt"></i>
            </div>

            <!-- Receipt details -->
            <div class="receipt-details">
              <div class="file-name">
                <strong>{{ receipt.originalFileName }}</strong>
              </div>
              <div class="upload-date">
                {{ formatDate(receipt.createdAt) }}
              </div>

              <!-- OCR summary -->
              <div class="ocr-summary">
                <span v-if="receipt.merchantName" class="merchant">{{ receipt.merchantName }}</span>
                <span v-if="receipt.totalAmount" class="total">
                  <v-money>{{ receipt.totalAmount }}</v-money>
                </span>
              </div>

              <!-- Top candidate hint -->
              <div v-if="receipt.topCandidate" class="top-candidate-hint">
                <small>
                  Suggested: {{ receipt.topCandidate.merchantName }}
                  <span v-if="receipt.topCandidate.amount">
                    <v-money>{{ receipt.topCandidate.amount }}</v-money>
                  </span>
                </small>
              </div>

              <!-- Warnings badge -->
              <div v-if="receipt.warnings && receipt.warnings.length" class="warnings-badge">
                <span class="badge">{{ receipt.warnings.length }} warning<span v-if="receipt.warnings.length > 1">s</span></span>
              </div>
            </div>

            <!-- Status and actions -->
            <div class="receipt-actions">
              <div class="status-badge">
                <span class="badge badge-success">Saved</span>
              </div>

              <!-- Action buttons -->
              <div class="action-buttons">
                <ed-button
                  @click="handleClaim(receipt)"
                  class="claim-btn"
                  :disabled="!canMutateReceipt(receipt)"
                  :title="readOnlyTooltip(receipt)">
                  Claim
                </ed-button>

                <!-- Delete (X) — gated cardholder-only on rows they own.
                     Admins viewing other users' receipts get no delete affordance
                     per v5.1 A6 read-only widening (can SEE but not MUTATE). -->
                <button
                  v-if="canMutateReceipt(receipt)"
                  type="button"
                  class="receipt-row-delete-btn"
                  @click="handleDelete(receipt)"
                  aria-label="Delete receipt"
                  title="Delete receipt">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </ed-notice>
      </div>

      <!-- Pagination (web) -->
      <div v-if="!isMobile && totalPages > 1" class="pagination-controls">
        <button
          v-for="page in totalPages"
          :key="page"
          @click="goToPage(page)"
          :class="{ active: currentPage === page }"
          class="page-btn"
        >
          {{ page }}
        </button>
      </div>

      <!-- Infinite scroll trigger (mobile) -->
      <div v-if="isMobile && hasMorePages" ref="infiniteScrollTrigger" class="infinite-scroll-trigger">
        <small v-if="!isLoadingMore">Scroll to load more</small>
        <small v-else>Loading more receipts...</small>
      </div>
    </div>

    <!-- Candidate Selector Modal (claim mode) -->
    <candidate-selector
      :show="showCandidateSelector"
      :receipt-scan-id="selectedReceiptId"
      :candidates="selectorCandidates"
      :is-mobile="isMobile"
      :row-version="selectorRowVersion"
      :auto-linked="selectorAutoLinked"
      :claim-mode="true"
      @claim-selected="handleClaimSelected"
      @claim-error="handleClaimError"
      @save-to-folder="handleSaveToFolder"
      @close="closeCandidateSelector"
    />

    <!-- Delete Confirmation Modal -->
    <ed-modal
      :show="showDeleteConfirm"
      title="Delete Receipt"
      :primary-button="'Confirm'"
      secondary-button="Cancel"
      @confirm="confirmDelete"
      @close="closeDeleteConfirm"
      type="medium"
    >
      <div slot="body">
        <p>Delete this receipt?</p>
      </div>
    </ed-modal>
  </div>
</template>

<script>
import featureFlags from '@/core/feature-flags/feature-flags'
import receiptScansApi from '@/core/api/receiptScans'
import CandidateSelector from '@/components/_modules/Programs/Card-transactions/CandidateSelector.vue'

export default {
  alias: 'evedpayreceiptfolder',
  components: {
    CandidateSelector
  },
  data() {
    return {
      featureFlags: featureFlags,
      receipts: null,
      imageUrlCache: {},
      isLoading: false,
      isRefreshing: false,
      isLoadingMore: false,
      currentPage: 1,
      pageSize: 25,
      totalPages: 1,
      hasMorePages: false,
      dateFormat: null,
      isMobile: false,
      lastRefreshTime: null,

      // Sort selection — 'UploadedAt' (CreatedAt desc) or 'TransactionDate' (OCR receipt date desc).
      // Maps to SearchReceiptsQuery.OrderBy on the server. Mobile-only control.
      sortBy: 'UploadedAt',

      // Candidate Selector state
      showCandidateSelector: false,
      selectedReceiptId: null,
      selectorCandidates: [],
      selectorRowVersion: null,
      selectorAutoLinked: false,

      // Delete confirmation state
      showDeleteConfirm: false,
      deleteTargetReceipt: null,

      // Pull-to-refresh state
      pullStartY: 0,
      pullDistance: 0,
      isPulling: false,

      // Swipe-to-delete state
      swipeStartX: 0,
      swipedRowId: null,
      swipeOffset: 0,

      // Highlight state
      highlightedReceiptId: null
    }
  },
  computed: {
    moment() {
      return global.moment
    }
  },
  created() {
    this.detectMobile()
    this.initializeData()
    window.addEventListener('resize', this.detectMobile)
    if (this.$route.query.highlight) {
      this.highlightedReceiptId = this.$route.query.highlight
    }
  },
  mounted() {
    if (this.isMobile) {
      this.$nextTick(this.setupInfiniteScroll)
    }
    if (this.highlightedReceiptId) {
      var self = this
      this.$nextTick(function () {
        var el = document.querySelector('.receipt-row.is-highlighted')
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }) }
        setTimeout(function () { self.highlightedReceiptId = null }, 2500)
      })
    }
  },
  updated() {
    if (this.isMobile && this._infiniteObserver && this.$refs.infiniteScrollTrigger) {
      this._infiniteObserver.observe(this.$refs.infiniteScrollTrigger)
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.detectMobile)
    if (this._infiniteObserver) {
      this._infiniteObserver.disconnect()
    }
  },
  methods: {
    detectMobile() {
      this.isMobile = global.config && global.config.layout === 'mobile'
    },

    async initializeData() {
      await this.fetchReceipts()
    },

    async _loadPage(page, { append = false } = {}) {
      // The folder lists only the current user's own receipts (PRODUCTDEV-5783) —
      // scoped server-side, no admin widening.
      var filters = { status: 'FolderSaved' }
      // orderBy/sortDirection map to SearchReceiptsQuery.OrderBy / SortDirection. Mobile-only:
      // desktop deliberately sends NO orderBy so its ordering stays exactly as Tudor's (the
      // server's omitted-orderBy default), independent of the mobile sort control.
      if (this.isMobile) {
        filters.orderBy = this.sortBy
        filters.sortDirection = 'desc'
      }

      var result = await receiptScansApi.searchReceipts(filters, page, this.pageSize)

      var items = result.items || []
      this.receipts = append ? this.receipts.concat(items) : items
      this.currentPage = page
      var totalCount = result.totalCount || 0
      this.totalPages = Math.ceil(totalCount / this.pageSize) || 1
      this.hasMorePages = this.currentPage < this.totalPages

      this._loadThumbnails(items)
    },

    _loadThumbnails(items) {
      var self = this
      items.forEach(function (receipt) {
        var id = receipt && receipt.receiptScanId
        if (!id || self.imageUrlCache[id]) return
        receiptScansApi.getImage(id)
          .then(function (res) {
            if (res && res.presignedUrl) {
              self.$set(self.imageUrlCache, id, res.presignedUrl)
            }
          })
          .catch(function () { /* missing thumb falls back to icon */ })
      })
    },

    // Presigned-URL expiry (~15 min) and CORS-blocked fetches both surface as <img> error.
    // Evict the cached URL so v-else falls through to the receipt icon instead of a broken image.
    onThumbnailError(receipt) {
      var id = receipt && receipt.receiptScanId
      if (!id) return
      this.$set(this.imageUrlCache, id, null)
    },

    async fetchReceipts() {
      this.isLoading = true
      try {
        await this._loadPage(this.currentPage)
        this.lastRefreshTime = new Date()
      } catch (error) {
        console.error('Error fetching receipts:', error)
        this.receipts = this.receipts || []
        this.$notifications.notify({
          type: 'danger',
          message: 'Failed to load receipts',
          placement: { from: 'top', align: 'center' }
        })
      } finally {
        this.isLoading = false
      }
    },

    async handleRefresh() {
      this.isRefreshing = true
      this.currentPage = 1
      try {
        await this.fetchReceipts()
        this.$notifications.notify({
          type: 'success',
          message: 'Receipts refreshed',
          placement: { from: 'top', align: 'center' }
        })
      } finally {
        this.isRefreshing = false
      }
    },

    setupInfiniteScroll() {
      var self = this
      this._infiniteObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && self.hasMorePages && !self.isLoadingMore) {
            self.loadMoreReceipts()
          }
        })
      })

      if (this.$refs.infiniteScrollTrigger) {
        this._infiniteObserver.observe(this.$refs.infiniteScrollTrigger)
      }
    },

    async loadMoreReceipts() {
      this.isLoadingMore = true
      try {
        await this._loadPage(this.currentPage + 1, { append: true })
      } catch (error) {
        console.error('Error loading more receipts:', error)
      } finally {
        this.isLoadingMore = false
      }
    },

    goToPage(page) {
      this.currentPage = page
      this.fetchReceipts()
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A'

      this.dateFormat = this.$user &&
        this.$user.sessionSettings &&
        this.$user.sessionSettings.DateAndTimeFormats &&
        this.$user.sessionSettings.DateAndTimeFormats.MomentDateFormat
        ? this.$user.sessionSettings.DateAndTimeFormats.MomentDateFormat
        : 'MM-DD-YYYY'

      try {
        return this.moment(dateStr).format(this.dateFormat)
      } catch (e) {
        return dateStr
      }
    },

    // The folder only lists the user's own receipts, so they can always claim/delete.
    canMutateReceipt(receipt) {
      return !!receipt
    },
    readOnlyTooltip() {
      return ''
    },

    async handleClaim(receipt) {
      try {
        var statusData = await receiptScansApi.getStatus(receipt.receiptScanId)

        var candidates = statusData.matchCandidates || statusData.candidates || []
        // Single auto-linked match — bypass selector and navigate directly
        if (statusData.autoLinked && candidates.length === 1) {
          this.$router.push({
            path: '/evedpay/transactiondetails',
            query: { transactionid: candidates[0].legacyTransactionId }
          })
          return
        }

        this.selectedReceiptId = receipt.receiptScanId
        this.selectorCandidates = candidates
        this.selectorRowVersion = statusData.rowVersion
        this.selectorAutoLinked = statusData.autoLinked || false
        this.showCandidateSelector = true
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.$notifications.notify({
            type: 'danger',
            message: 'You no longer have access to this receipt',
            placement: { from: 'top', align: 'center' }
          })
          this.$router.push('/evedpay/receiptfolder')
        } else if (error.response && error.response.status === 409) {
          this.$notifications.notify({
            type: 'warning',
            message: 'Receipt state changed — pick again',
            placement: { from: 'top', align: 'center' }
          })
          await this.fetchReceipts()
          var refreshed = this.receipts && this.receipts.find(function (r) {
            return r.receiptScanId === receipt.receiptScanId
          })
          if (refreshed) {
            try {
              var freshStatus = await receiptScansApi.getStatus(receipt.receiptScanId)
              var freshCandidates = freshStatus.matchCandidates || freshStatus.candidates || []
              // Single auto-linked match — bypass selector and navigate directly
              if (freshStatus.autoLinked && freshCandidates.length === 1) {
                this.$router.push({
                  path: '/evedpay/transactiondetails',
                  query: { transactionid: freshCandidates[0].legacyTransactionId }
                })
                return
              }
              this.selectedReceiptId = receipt.receiptScanId
              this.selectorCandidates = freshCandidates
              this.selectorRowVersion = freshStatus.rowVersion
              this.selectorAutoLinked = freshStatus.autoLinked || false
              this.showCandidateSelector = true
            } catch (reopenError) {
              // If fresh fetch also fails, fall through silently — user can re-tap Claim
            }
          }
        } else {
          this.$notifications.notify({
            type: 'danger',
            message: 'Failed to load candidate transactions',
            placement: { from: 'top', align: 'center' }
          })
        }
      }
    },

    handleClaimSelected(event) {
      this.closeCandidateSelector()
      this.$router.push({
        path: '/evedpay/transactiondetails',
        query: {
          transactionid: event.legacyTransactionId
        }
      })
    },

    async handleClaimError(event) {
      if (event.status === 403) {
        this.closeCandidateSelector()
        this.$router.push('/evedpay/receiptfolder')
      } else if (event.status === 409) {
        var receiptId = this.selectedReceiptId
        this.closeCandidateSelector()
        await this.fetchReceipts()
        await this.handleClaim({ receiptScanId: receiptId })
      } else {
        this.closeCandidateSelector()
      }
    },

    handleSaveToFolder() {
      this.closeCandidateSelector()
      this.fetchReceipts()
    },

    closeCandidateSelector() {
      this.showCandidateSelector = false
      this.selectedReceiptId = null
      this.selectorCandidates = []
      this.selectorRowVersion = null
      this.selectorAutoLinked = false
    },

    handleDelete(receipt) {
      this.deleteTargetReceipt = receipt
      this.showDeleteConfirm = true
    },

    async confirmDelete() {
      if (!this.deleteTargetReceipt) return

      const receiptId = this.deleteTargetReceipt.receiptScanId
      this.closeDeleteConfirm()

      try {
        await receiptScansApi.deleteReceipt(receiptId)
        // Remove row from list
        this.receipts = this.receipts.filter(r => r.receiptScanId !== receiptId)
        this.$notifications.notify({
          type: 'success',
          message: 'Receipt deleted',
          placement: { from: 'top', align: 'center' }
        })
      } catch (error) {
        if (error.response && error.response.status === 409) {
          // Receipt is no longer in folder, refresh list
          this.$notifications.notify({
            type: 'warning',
            message: 'Receipt is no longer in your folder',
            placement: { from: 'top', align: 'center' }
          })
          await this.fetchReceipts()
        } else if (error.response && error.response.status === 403) {
          this.$notifications.notify({
            type: 'danger',
            message: "You can't delete this receipt",
            placement: { from: 'top', align: 'center' }
          })
        } else {
          console.error('Error deleting receipt:', error)
          this.$notifications.notify({
            type: 'danger',
            message: 'Failed to delete receipt',
            placement: { from: 'top', align: 'center' }
          })
        }
      }
    },

    closeDeleteConfirm() {
      this.showDeleteConfirm = false
      this.deleteTargetReceipt = null
    },

    handleUploadClick() {
      this.$router.push({ path: '/evedpay/transactions' })
    },

    // Pull-to-refresh methods
    onPullStart: function (e) {
      if (!this.isMobile || this.isRefreshing) return
      var container = e.currentTarget
      if (container.scrollTop > 0) return
      this.pullStartY = e.touches[0].clientY
      this.isPulling = true
    },

    onPullMove: function (e) {
      if (!this.isPulling) return
      var deltaY = e.touches[0].clientY - this.pullStartY
      var deltaX = Math.abs(e.touches[0].clientX - (this._pullStartX || e.touches[0].clientX))
      if (!this._pullStartX) this._pullStartX = e.touches[0].clientX
      if (deltaX > deltaY) { this.isPulling = false; this.pullDistance = 0; return }
      if (deltaY > 0) {
        this.pullDistance = Math.min(deltaY * 0.5, 100)
        e.preventDefault()
      }
    },

    onPullEnd: function () {
      if (!this.isPulling) return
      this._pullStartX = null
      if (this.pullDistance >= 60) {
        this.handleRefresh()
      }
      this.isPulling = false
      this.pullDistance = 0
    },

    // Swipe-to-delete methods
    onRowSwipeStart: function (e, receipt) {
      this.swipeStartX = e.touches[0].clientX
      this._swipeStartY = e.touches[0].clientY
      this.swipedRowId = receipt.receiptScanId
      this.swipeOffset = 0
    },

    onRowSwipeMove: function (e, receipt) {
      if (this.swipedRowId !== receipt.receiptScanId) return
      var deltaX = e.touches[0].clientX - this.swipeStartX
      var deltaY = Math.abs(e.touches[0].clientY - this._swipeStartY)
      if (deltaY > Math.abs(deltaX)) { this.swipedRowId = null; this.swipeOffset = 0; return }
      if (deltaX < 0) {
        this.swipeOffset = Math.max(deltaX, -100)
        e.preventDefault()
      }
    },

    onRowSwipeEnd: function (e, receipt) {
      if (this.swipedRowId !== receipt.receiptScanId) return
      if (this.swipeOffset < -60) {
        this.swipeOffset = -80
      } else {
        this.swipeOffset = 0
        this.swipedRowId = null
      }
    },

    getSwipeStyle: function (receiptScanId) {
      if (this.swipedRowId === receiptScanId && this.swipeOffset !== 0) {
        return {
          transform: 'translateX(' + this.swipeOffset + 'px)',
          transition: 'transform 0.2s ease'
        }
      }
      return { transform: 'translateX(0)', transition: 'transform 0.2s ease' }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/assets/sass/_variables.scss";

.receipt-folder-container {
  padding: 0;
}

/* Same row layout as the cardtransactions Back bar (purple bg/colors come from
 * the mobile layout's global .navless-layout-header theme styles). */
.navless-layout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.feature-disabled {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.no-data {
  height: calc(100vh - 15em);
  width: 75%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  > div {
    width: 100%;
  }

  h3 {
    margin-top: 1.6em;
    margin-bottom: 0.2em;
    font-weight: 600;
  }

  h3, p, .icon {
    color: #4e2683;
  }

  p {
    line-height: 1.4em;
    margin-bottom: 1.5em;
  }

  .icon {
    font-size: 5em;
    position: relative;

    &:after {
      position: absolute;
      display: block;
      content: '';
      left: 50%;
      top: 50%;
      margin-top: -80px;
      margin-left: -80px;
      width: 160px;
      height: 160px;
      border-radius: 50%;
      background: rgba(78, 38, 131, 0.15);
      z-index: -1;
    }
  }

  .upload-cta {
    margin-top: 1rem;
  }
}

.loading-state {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.pull-indicator {
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: height 0.2s ease;
  color: #999;
  font-size: 13px;

  .pull-indicator-content {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 0;

    i {
      font-size: 14px;
    }
  }
}

.refresh-controls {
  text-align: right;
  padding: 1rem;
  margin-bottom: 1rem;
}

.sort-controls {
  padding: 0 1rem 0.75rem;

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 13px;
    color: #555;
    margin: 0;
  }

  &__select {
    padding: 4px 8px;
    font-size: 13px;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    background: #fff;
  }
}

.receipt-row {
  margin-bottom: 1rem;

  &.is-processing {
    opacity: 0.7;
  }
}

.receipt-notice {
  border-radius: 6px;
  box-shadow: 1px 3px 5px 0px rgba(0, 0, 0, 0.15);
  border-color: #f2f2f2 !important;
  padding: 1rem;

  >>> &.no-icon .icon-wrapper.no-icon {
    min-width: 0;
    display: none;
  }
}

.receipt-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.receipt-thumbnail {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  background: linear-gradient(315deg, #8c407b 0%, #63417e 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .icon {
    color: #fff;
    font-size: 28px;
  }

  .receipt-thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.receipt-details {
  flex: 1;
  min-width: 0;

  .file-name {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 0.25rem;
    word-break: break-word;
  }

  .upload-date {
    font-size: 12px;
    color: #666;
    margin-bottom: 0.5rem;
  }

  .ocr-summary {
    font-size: 13px;
    color: #333;
    margin-bottom: 0.25rem;

    .merchant {
      font-weight: 500;
    }

    .total {
      margin-left: 0.5rem;
      font-family: 'Courier New', monospace;
    }
  }

  .top-candidate-hint {
    font-size: 12px;
    color: #4a90e2;
    margin-bottom: 0.25rem;
  }

  .warnings-badge {
    font-size: 12px;

    .badge {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 3px;
      background-color: #fff3cd;
      color: #856404;
      font-weight: 500;
    }
  }
}

.receipt-actions {
  flex-shrink: 0;
  text-align: right;

  .status-badge {
    margin-bottom: 0.5rem;

    .badge {
      display: inline-block;
      padding: 4px 8px;
      border-radius: 3px;
      font-size: 11px;
      font-weight: 600;

      &.badge-success {
        background-color: #d4edda;
        color: #155724;
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    position: relative;

    .claim-btn {
      min-width: 80px;
    }

    .receipt-row-delete-btn {
      background: none;
      border: none;
      padding: 0.5rem 0.6rem;
      cursor: pointer;
      color: #888;
      font-size: 16px;
      line-height: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: color 0.15s ease, background-color 0.15s ease;

      &:hover {
        color: #d32f2f;
        background-color: #fdecea;
      }

      &:focus {
        outline: 2px solid #4a90e2;
        outline-offset: 1px;
      }
    }

  }
}

.swipe-container {
  position: relative;
  overflow: hidden;
  border-radius: 6px;
}

.swipe-delete-action {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 80px;
  background: #d32f2f;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  border-radius: 0 6px 6px 0;

  i {
    font-size: 18px;
    margin-bottom: 4px;
  }
}

.swipe-content {
  position: relative;
  z-index: 1;
  background: #fff;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
  padding: 1rem;

  .page-btn {
    padding: 0.5rem 0.75rem;
    border: 1px solid #ddd;
    background: #fff;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;

    &:hover {
      border-color: #4a90e2;
      color: #4a90e2;
    }

    &.active {
      background-color: #4a90e2;
      color: #fff;
      border-color: #4a90e2;
    }
  }
}

.infinite-scroll-trigger {
  text-align: center;
  padding: 1.5rem;
  color: #999;
  font-size: 12px;
}

.receipt-row.is-highlighted {
  animation: highlight-fade 2.5s ease;
}

@keyframes highlight-fade {
  0% { background-color: rgba(74, 144, 226, 0.25); }
  70% { background-color: rgba(74, 144, 226, 0.15); }
  100% { background-color: transparent; }
}

// Mobile adjustments
@media (max-width: 768px) {
  .receipt-content {
    flex-direction: column;
  }

  .receipt-thumbnail {
    width: 100%;
    height: 80px;
  }

  .receipt-actions {
    text-align: left;
    margin-top: 0.5rem;

    .action-buttons {
      justify-content: flex-start;

      .claim-btn {
        flex: 1;
      }
    }
  }

  .pagination-controls {
    display: none;
  }

  .no-data {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/_modules/EvedPay/ReceiptFolder.vue